import { useEffect, useRef, useState } from 'react';
import {
  contractWorklogSchema,
  contractWorklogSummarySchema,
  formatSort,
  type ContractWorklog,
  type ContractWorklogSummary,
} from '@forecastify/shared';
import type { PagerState, SortState } from '@forecastify/ui';
import { parseList, totalOf } from '../permissions-hook';
import { readAll } from '../read-all';
import { pagerOf, usePages } from '../use-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 31 Oct 2026 · One period's logs, paged, filtered and sorted on the server. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The period detail held every worklog of the contract — one read that stopped at 5,000 without
 * saying so — and cut its window, filtered, sorted and paged them in the browser. Now the window and
 * the filters travel as query parameters, the page comes back with the filtered total, and the
 * figures the screen showed from the rows it held (the window's hours and people, the footer, the
 * filters' options) come from `/worklogs/summary`.
 *
 * ⚠️⚠️ **A GROUPED VIEW STILL READS ITS WHOLE FILTERED SET**, because a group's header carries the
 * group's whole total and a grouped list is not paged (`period-detail.tsx` argues both). The set is
 * one period of one contract, through `readAll` — the window bounds it, not the contract.
 *
 * ⚠️ **Every filter change goes back to page one** — `use-pages.ts`'s trap.
 */

/** The logs table's page size — the one its hand-built predecessor used, kept. */
export const PERIOD_LOG_PAGE = 25;

/** The line item filter's value for the collector's logs — the server's `CONTRACT_LEVEL_ITEM`. */
export const CONTRACT_LEVEL = 'contract';

export const PERIOD_GROUPS = ['none', 'member', 'lineItem', 'day', 'task'] as const;
export type PeriodGroup = (typeof PERIOD_GROUPS)[number];

interface Session {
  readonly workspaceId: string | null;
  readonly request: (path: string, init?: RequestInit) => Promise<Response>;
}

export interface PeriodLogs {
  /** The page — or, grouped, every row the filters left. */
  readonly rows: readonly ContractWorklog[];
  readonly page: PagerState;
  /** `null` until it arrives, or when it could not be read. */
  readonly summary: ContractWorklogSummary | null;
  readonly loading: boolean;
  readonly query: string;
  readonly sort: readonly SortState[];
  readonly person: string;
  readonly task: string;
  readonly item: string;
  readonly group: PeriodGroup;
  readonly setQuery: (value: string) => void;
  readonly setSort: (value: readonly SortState[]) => void;
  readonly setPerson: (value: string) => void;
  readonly setTask: (value: string) => void;
  readonly setItem: (value: string) => void;
  readonly setGroup: (value: PeriodGroup) => void;
}

export function usePeriodLogs(
  session: Session,
  contractId: string | null,
  window: { readonly from: string; readonly to: string } | null,
  /** The route's live counter — a colleague logging an hour re-reads the page. */
  liveNonce: number,
): PeriodLogs {
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const pages = usePages(PERIOD_LOG_PAGE);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [person, setPerson] = useState('');
  const [task, setTask] = useState('');
  const [item, setItem] = useState('');
  const [group, setGroup] = useState<PeriodGroup>('none');
  const [rows, setRows] = useState<readonly ContractWorklog[]>([]);
  const [summary, setSummary] = useState<ContractWorklogSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const from = window?.from ?? null;
  const to = window?.to ?? null;
  const settle = pages.settle;

  useEffect(() => {
    if (contractId === null || from === null || to === null) return;
    const params = new URLSearchParams({ from, to });
    if (query.trim() !== '') params.set('search', query.trim());
    if (person !== '') params.set('memberId', person);
    if (task !== '') params.set('taskId', task);
    if (item !== '') params.set('lineItemId', item);
    const order = formatSort(sort);
    if (order !== undefined) params.set('sort', order);
    const base = `/contracts/${encodeURIComponent(contractId)}/worklogs`;

    const run = { live: true };
    setLoading(true);
    void (async () => {
      const current = sessionRef.current;
      const summaryRead = current
        .request(`${base}/summary?${params.toString()}`)
        .then(async (response) =>
          response.ok
            ? (contractWorklogSummarySchema.safeParse(await response.json()).data ?? null)
            : null,
        )
        .catch(() => null);

      let next: readonly ContractWorklog[] = [];
      let total = 0;
      if (group === 'none') {
        params.set('limit', String(PERIOD_LOG_PAGE));
        if (pages.offset > 0) params.set('offset', String(pages.offset));
        const response = await current.request(`${base}?${params.toString()}`).catch(() => null);
        if (response?.ok === true) {
          const body: unknown = await response.json().catch(() => null);
          next = parseList(contractWorklogSchema, body) ?? [];
          total = totalOf(body);
        }
      } else {
        next =
          (await readAll(current, `${base}?${params.toString()}`, contractWorklogSchema).catch(
            () => null,
          )) ?? [];
        total = next.length;
      }
      const read = await summaryRead;
      if (!run.live) return;
      setRows(next);
      settle(total);
      setSummary(read);
      setLoading(false);
    })();
    return () => {
      run.live = false;
    };
  }, [
    contractId,
    from,
    to,
    query,
    person,
    task,
    item,
    sort,
    group,
    pages.offset,
    settle,
    liveNonce,
  ]);

  /* ⚠️ A different period is a different set — back to page one with no filters carried over. */
  const reset = pages.reset;
  useEffect(() => {
    reset();
  }, [contractId, from, to, reset]);

  const resetting =
    <T>(set: (value: T) => void) =>
    (value: T): void => {
      set(value);
      reset();
    };

  return {
    rows,
    page: pagerOf(pages),
    summary,
    loading,
    query,
    sort,
    person,
    task,
    item,
    group,
    setQuery: resetting(setQuery),
    setSort: resetting(setSort),
    setPerson: resetting(setPerson),
    setTask: resetting(setTask),
    setItem: resetting(setItem),
    setGroup: resetting(setGroup),
  };
}
